import type { PartnerOrganizationLead } from "@entities";
import { PartnerOrganizationLead as PartnerOrganizationLeadEntity } from "@entities";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { Box } from "@mui/material";
import { partnerOrganizationLeadRepo } from "@repositories";
import {
  partnerOrgLeadContactChannelLabel,
  partnerOrgLeadToOrganizationPrefill,
} from "@shared/helpers/partner-organization-lead-contact";
import { useCreateHyperTable } from "@shared/frontend/hyper-table";
import { type ActionButton } from "@shared/frontend/hyper-table/inner-components/hyper-table-actions-bar/ActionsBar";
import type { SingleRowActionGetter } from "@shared/frontend/hyper-table/inner-components/hyper-table-menu-actions-column/HyperTableMenuActionsColumn";
import { SingleRowAction } from "@shared/frontend/hyper-table/inner-components/hyper-table-expand-column/HyperTableExpandColumn";
import { type ColumnProps } from "@shared/frontend/hyper-table/types";
import { useCallback, useMemo, useRef, useState } from "react";
import { remult, type EntityFilter } from "remult";
import { Roles } from "@shared/Roles";
import { useAlert } from "../../../contexts/AlertProvider";
import useQuestion from "../../../dialog-components/QuestionDialog";
import {
  PartnerOrganizationDialog,
  type PartnerOrganizationSavedResult,
} from "./PartnerOrganizationDialog";
import {
  organizationLeadFilterItems,
  PARTNER_ORGANIZATION_LEAD_FILTERS,
} from "../organization-lead-filters";
import { PartnerOrganizationLeadDialog } from "./PartnerOrganizationLeadDialog";
import type { PartnerOrganizationFormValues } from "./partner-organization-form";

const LEAD_COLUMNS: ColumnProps<PartnerOrganizationLead>[] = [
  {
    field: "name",
    caption: "שם הארגון",
    enableSorting: true,
    minSize: 220,
  },
  {
    field: "handlingManagerId",
    caption: "מנהל מלווה",
    minSize: 180,
    cell: ({ row }) => row.original.handlingManager?.name ?? "",
  },
  {
    field: "phoneNumber",
    caption: "טלפון",
    minSize: 140,
    cell: ({ row }) => row.original.phoneNumber ?? "",
  },
  {
    field: "email",
    caption: "מייל",
    minSize: 180,
    cell: ({ row }) => row.original.email ?? "",
  },
  {
    field: "contactRole",
    caption: "תפקיד",
    minSize: 140,
    cell: ({ row }) => row.original.contactRole ?? "",
  },
  {
    field: "notes",
    caption: "הערות",
    minSize: 160,
    cell: ({ row }) => row.original.notes?.slice(0, 80) ?? "",
  },
  {
    field: "contactChannel",
    caption: "דרך יצירת קשר",
    minSize: 140,
    cell: ({ row }) =>
      partnerOrgLeadContactChannelLabel(row.original.contactChannel),
  },
  {
    field: "createdAt",
    caption: "תאריך יצירה",
    enableSorting: true,
    minSize: 140,
    cell: ({ row }) =>
      row.original.createdAt
        ? new Date(row.original.createdAt).toLocaleDateString("he-IL")
        : "",
  },
];

type Props = {
  onOrganizationCreated?: () => void | Promise<void>;
};

export function PartnerOrganizationLeadsTable({ onOrganizationCreated }: Props) {
  const { HyperTable } = useCreateHyperTable(PartnerOrganizationLeadEntity);
  const canManage = remult.isAllowed(Roles.SuperAdmins);
  const { setAlertText, setSeverity, setTime } = useAlert();
  const question = useQuestion();
  const [leadDialogOpen, setLeadDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<PartnerOrganizationLead>();
  const [convertingLead, setConvertingLead] = useState<PartnerOrganizationLead>();
  const leadConvertedRef = useRef(false);

  const openAdd = useCallback(() => {
    setSelectedLead(undefined);
    setLeadDialogOpen(true);
  }, []);

  const openEdit = useCallback((lead: PartnerOrganizationLead) => {
    setSelectedLead(lead);
    setLeadDialogOpen(true);
  }, []);

  const closeLeadDialog = useCallback(() => {
    setLeadDialogOpen(false);
    setSelectedLead(undefined);
  }, []);

  const deleteLeads = useCallback(
    async (
      where: EntityFilter<PartnerOrganizationLead>,
      successMessage = "הלידים נמחקו בהצלחה",
    ) => {
      try {
        await partnerOrganizationLeadRepo.deleteMany({ where });
        setAlertText(successMessage);
        setSeverity("success");
        setTime(2000);
        return true;
      } catch {
        setAlertText("מחיקה נכשלה");
        setSeverity("error");
        setTime(3000);
        return false;
      }
    },
    [setAlertText, setSeverity, setTime],
  );

  const getMenuActions = useCallback<SingleRowActionGetter<PartnerOrganizationLead>>(
    () => {
      const actions: SingleRowAction<PartnerOrganizationLead>[] = [];
      if (canManage) {
        actions.push(
          {
            text: "הפוך לארגון שותף",
            icon: <BusinessOutlinedIcon />,
            onClick: (_: unknown, row: { original: PartnerOrganizationLead }) => {
              leadConvertedRef.current = false;
              setConvertingLead(row.original);
              return { refetch: false };
            },
          },
          {
            text: "עריכה",
            icon: <EditIcon />,
            onClick: (_: unknown, row: { original: PartnerOrganizationLead }) => {
              openEdit(row.original);
              return { refetch: false };
            },
          },
          {
            text: "מחיקה",
            icon: <DeleteOutlineIcon />,
            onClick: async (
              _: unknown,
              row: { original: PartnerOrganizationLead },
            ) => {
              const yes = await question(
                `האם למחוק את הליד "${row.original.name}"?`,
              );
              if (!yes) return { refetch: false };
              const deleted = await deleteLeads(
                { id: row.original.id },
                "הליד נמחק בהצלחה",
              );
              return { refetch: deleted };
            },
          },
        );
      }
      return actions;
    },
    [canManage, deleteLeads, openEdit, question],
  );

  const buttons = useMemo<ActionButton<PartnerOrganizationLead>[]>(
    () => [
      {
        icon: <DeleteOutlineIcon />,
        title: "מחיקה",
        onClick: async ({ whereToUpdate }) => {
          const yes = await question("האם למחוק את הלידים שנבחרו?");
          if (!yes) return { refetch: false };
          const deleted = await deleteLeads(whereToUpdate);
          return { refetch: deleted, resetSelection: deleted };
        },
      },
    ],
    [deleteLeads, question],
  );

  const convertDefaults = useMemo<
    Partial<PartnerOrganizationFormValues> | undefined
  >(
    () =>
      convertingLead
        ? partnerOrgLeadToOrganizationPrefill(convertingLead)
        : undefined,
    [convertingLead],
  );

  return (
    <Box
      sx={{
        display: "flex",
        flex: "1 1 0",
        flexDirection: "column",
        minHeight: 0,
        width: "100%",
        height: "100%",
      }}
    >
      <HyperTable
        tableId="partner-organization-leads-table"
        title={<Box sx={{ flex: 1 }} />}
        components={{
          ...(canManage
            ? {
                addRowButton: {
                  onClick: openAdd,
                  content: "ליד חדש",
                },
              }
            : {}),
        }}
        findConfig={{
          initialOrderBy: { createdAt: "desc" },
          where: { convertedAt: null } as unknown as EntityFilter<PartnerOrganizationLead>,
          include: { handlingManager: true },
        }}
        onRowClick={
          canManage ? (_, row) => openEdit(row.original) : undefined
        }
      >
        <HyperTable.Toolbar disableViewOptions>
          {organizationLeadFilterItems(PARTNER_ORGANIZATION_LEAD_FILTERS)}
        </HyperTable.Toolbar>
        {canManage && <HyperTable.SelectColumn />}
        <HyperTable.MenuColumn actions={getMenuActions} />
        {LEAD_COLUMNS.map((column) => (
          <HyperTable.Column key={column.field} {...column} />
        ))}
        {canManage && <HyperTable.ActionsBar buttons={buttons} />}
      </HyperTable>

      {canManage && leadDialogOpen && (
        <PartnerOrganizationLeadDialog
          open={leadDialogOpen}
          lead={selectedLead ?? null}
          onClose={closeLeadDialog}
          onSaved={async () => {
            await HyperTable.context.current?.refetch("invalidate");
            setAlertText(
              selectedLead ? "הליד עודכן בהצלחה" : "הליד נוצר בהצלחה",
            );
            setSeverity("success");
            setTime(2000);
          }}
        />
      )}

      {canManage && convertingLead && (
        <PartnerOrganizationDialog
          open
          organization={null}
          createDefaults={convertDefaults}
          onClose={() => setConvertingLead(undefined)}
          onSaved={async (result: PartnerOrganizationSavedResult) => {
            if (result.created && !leadConvertedRef.current) {
              leadConvertedRef.current = true;
              await partnerOrganizationLeadRepo.update(convertingLead.id, {
                partnerOrganizationId: result.organization.id,
                convertedAt: new Date(),
              });
              await HyperTable.context.current?.refetch("invalidate");
              await onOrganizationCreated?.();
              setAlertText("הליד הומר לארגון שותף");
              setSeverity("success");
              setTime(2000);
            }
            if (result.close) {
              setConvertingLead(undefined);
            }
          }}
        />
      )}
    </Box>
  );
}
